import { useRef } from "react";
import { Paperclip, X, Loader2, AlertCircle } from "lucide-react";
import type { Attachment } from "../types";

interface AttachmentBarProps {
  attachments: Attachment[];
  stagedFiles: File[];
  isUploading: boolean;
  uploadError: string | null;
  onFilesSelected: (files: File[]) => void;
  onDelete: (id: string) => void;
  onRemoveStaged: (index: number) => void;
}

const ACCEPTED = ".pdf,.doc,.docx,.txt,.md,.csv,.json,.png,.jpg,.jpeg,.webp,.gif";

export function AttachmentBar({
  attachments,
  stagedFiles,
  isUploading,
  uploadError,
  onFilesSelected,
  onDelete,
  onRemoveStaged,
}: AttachmentBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  const isEmpty = attachments.length === 0 && stagedFiles.length === 0 && !isUploading && !uploadError;

  return (
    <div className={`rb-attachment-bar${isEmpty ? " rb-attachment-bar--empty" : ""}`}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        multiple
        style={{ display: "none" }}
        onChange={handleChange}
      />

      <button
        className="rb-attachment-attach-btn"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        title="Attach files (PDF, images, documents)"
      >
        {isUploading ? (
          <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
        ) : (
          <Paperclip style={{ width: 12, height: 12 }} />
        )}
        <span>{isUploading ? "Uploading…" : "Attach"}</span>
      </button>

      {uploadError && (
        <div className="rb-attachment-error">
          <AlertCircle style={{ width: 11, height: 11, flexShrink: 0 }} />
          <span>{uploadError}</span>
        </div>
      )}

      {(stagedFiles.length > 0 || attachments.length > 0) && (
        <div className="rb-attachment-chips">
          {stagedFiles.map((f, i) => (
            <div
              key={`staged-${i}`}
              className="rb-attachment-chip rb-attachment-chip--staged"
              title="Will upload when you send a message"
            >
              <span className="rb-attachment-chip-name">{f.name}</span>
              <button
                className="rb-attachment-chip-remove"
                onClick={() => onRemoveStaged(i)}
                title={`Remove ${f.name}`}
              >
                <X style={{ width: 9, height: 9 }} />
              </button>
            </div>
          ))}
          {attachments.map((a) => (
            <div
              key={a.id}
              className="rb-attachment-chip"
              title={a.contentLength ? `${a.contentLength.toLocaleString()} chars` : a.filename}
            >
              <span className="rb-attachment-chip-name">{a.filename}</span>
              <button
                className="rb-attachment-chip-remove"
                onClick={() => onDelete(a.id)}
                title={`Remove ${a.filename}`}
              >
                <X style={{ width: 9, height: 9 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {stagedFiles.length > 0 && !isUploading && (
        <span className="rb-attachment-staged-hint">↑ sends with message</span>
      )}
    </div>
  );
}
